import { useGetChurnScores, useResolveChurn } from '@actions';
import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import ChurnRiskBadge from '@components/V4/ChurnRisk/ChurnRiskBadge';
import Table, { SortOrder } from '@components/V4/Table/Table';
import { useQueryParamsState } from '@hooks';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import useDetailsModal from '@modules/Admin/hooks/useDetailsModal';
import { ChurnSortField } from '@shared/apis/churn.apis';
import { ChurnListRowDTO } from '@shared/clientTypes/DTOs/ChurnDTO';
import { message, Tooltip } from 'antd';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment from 'moment';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ChurnAccuracyPanel from './ChurnAccuracyPanel';

type SortField = ChurnSortField;

const useQuery = () => new URLSearchParams(useLocation().search);

type SearchParams = {
  page: string;
  limit: string;
  sortBy: SortField;
  sortOrder: SortOrder;
  search: string;
  studentId: string;
  gradYear: number;
  contractSize: number;
  timeWithUs: string;
  userActiveStatus: 'indeterminate' | 'checked' | 'unchecked';
  graduationYears: string[];
  averageScoreLevel: 'low' | 'medium' | 'high';
  timeWithUsStart: string | undefined;
  timeWithUsEnd: string | undefined;
  resolvedStatus: 'indeterminate' | 'checked' | 'unchecked';
};

const formatTimeWithUs = (days: number | null | undefined) => {
  if (days == null || isNaN(days)) return '-';
  const duration = moment.duration(days, 'days');
  const years = duration.years();
  const months = duration.months();
  const remDays = duration.days();
  const parts = [];
  if (years > 0) parts.push(`${years} y`);
  if (months > 0) parts.push(`${months} m`);
  if (remDays > 0 || parts.length === 0) parts.push(`${remDays.toFixed(0)} d`);
  return parts.join(', ');
};

export function Churn() {
  const query = useQuery();

  const page = query.get('page') || '1';
  const limit = query.get('limit') || '10';
  const sortBy = (query.get('sortBy') as SortField) || 'score';
  const sortOrder = (query.get('sortOrder') as SortOrder) || 'desc';
  const search = query.get('search') || '';
  const studentId = query.get('studentId') || '';
  const userActiveStatus = (query.get('userActiveStatus') || 'indeterminate') as
    | 'indeterminate'
    | 'checked'
    | 'unchecked';

  const graduationYears = query.get('graduationYears')?.split(',') ?? [];
  const averageScoreLevel = query.get('averageScoreLevel') as 'low' | 'medium' | 'high';
  const timeWithUsStart = query.get('timeWithUsStart') || undefined;
  const timeWithUsEnd = query.get('timeWithUsEnd') || undefined;
  const resolvedStatus = query.get('resolvedStatus') as 'indeterminate' | 'checked' | 'unchecked';

  const [resolvingIds, setResolvingIds] = useState<string[]>([]);

  const {
    activeModalView,
    isModalOpen,
    studentId: detailsModalStudentId,
    mentorId: detailsModalMentorId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'student' });

  const [searchParams, setSearchParams] = useQueryParamsState<SearchParams>({
    page,
    limit,
    sortBy,
    sortOrder,
    search,
    studentId,
    userActiveStatus,
    graduationYears,
    averageScoreLevel,
    timeWithUsStart,
    timeWithUsEnd,
    resolvedStatus,
  });

  const {
    data: churnScores,
    isLoading,
    refetch,
  } = useGetChurnScores(
    {
      page: searchParams.page,
      limit: searchParams.limit,
      sortBy: searchParams.sortBy,
      sortOrder: searchParams.sortOrder || undefined,
      search: searchParams.search,
      userId: searchParams.studentId,
      userActiveStatus: searchParams.userActiveStatus,
      graduationYears: searchParams.graduationYears?.length
        ? searchParams.graduationYears
        : undefined,
      averageScoreLevel: searchParams.averageScoreLevel || undefined,
      timeWithUsStart: searchParams.timeWithUsStart
        ? Number(searchParams.timeWithUsStart)
        : undefined,
      timeWithUsEnd: searchParams.timeWithUsEnd ? Number(searchParams.timeWithUsEnd) : undefined,
      resolvedStatus: searchParams.resolvedStatus,
    },
    {
      onSuccess: () => {
        resolvingIds.map((id) => {
          message.success(`Churn score has successfully been marked as resolved.`);
        });
        setResolvingIds([]);
      },
    }
  );

  const { mutate: resolveChurn } = useResolveChurn({
    onSuccess: () => {
      refetch();
    },
  });

  const showMoreHandler = (studentId: string) => {
    openModal(activeModalView, {
      studentId: studentId,
    });
  };

  const sortChangeHandler = (sortBy: SortField | null, sortOrder: SortOrder) => {
    setSearchParams({
      sortBy: sortBy || 'score',
      sortOrder: sortOrder || 'desc',
      page: '1',
    });
  };

  const pageChangeHandler = (page: number) => {
    setSearchParams({ page: page.toString() });
  };

  const pageSizeChangeHandler = (limit: number) => {
    setSearchParams({ limit: limit.toString(), page: '1' });
  };

  const markAsResolved = async (churnScoreId: string) => {
    setResolvingIds((prev) => [...prev, churnScoreId]);
    resolveChurn([churnScoreId], {});
  };

  const relativeDay = (value?: string) => (value ? moment(value).fromNow() : 'never');

  const renderDrivers = (row: ChurnListRowDTO) => {
    if (!row.drivers.length) return <span className="text-customGrey">No risk signals</span>;

    return (
      <Tooltip
        overlayClassName="max-w-md"
        title={
          <div className="flex flex-col gap-1.5">
            {row.drivers.map((driver) => (
              <div key={driver.key} className="flex flex-col">
                <span className="font-semibold">
                  {driver.label} (+{driver.points})
                </span>
                <span className="opacity-80">{driver.detail}</span>
              </div>
            ))}
          </div>
        }
      >
        <span className="cursor-help">
          {row.drivers
            .slice(0, 2)
            .map((driver) => `${driver.label} (+${driver.points})`)
            .join(' · ')}
          {row.drivers.length > 2 ? (
            <span className="text-customGrey"> +{row.drivers.length - 2} more</span>
          ) : null}
        </span>
      </Tooltip>
    );
  };

  const headerCell = (label: string, field?: SortField, className = 'w-[10%] truncate') =>
    field ? (
      <Table.HeaderCell
        className={className}
        onSort={(order) => sortChangeHandler(field, order)}
        sortOrder={sortBy === field ? sortOrder : undefined}
      >
        {label}
      </Table.HeaderCell>
    ) : (
      <Table.HeaderCell className={className}>{label}</Table.HeaderCell>
    );

  return (
    <div className="p-6 py-8">
      <ChurnAccuracyPanel />
      <Table
        className="table-fixed"
        pagination={{
          pageSize: Number(limit),
          total: churnScores?.total,
          currentPage: Number(page),
          onCurrentPageChange: pageChangeHandler,
          onPageSizeChange: pageSizeChangeHandler,
        }}
      >
        <Table.Header>
          <Table.Row isHeader>
            {headerCell('Name', 'fullName', 'w-[12%] truncate')}
            {headerCell('Grad Year', 'gradYear', 'w-[6%] truncate')}
            {headerCell('Churn Risk', 'score', 'w-[12%] truncate')}
            {headerCell('30d', undefined, 'w-[5%] truncate')}
            {headerCell('Why', undefined, 'w-[22%] truncate')}
            {headerCell('Last Session', undefined, 'w-[8%] truncate')}
            {headerCell('Next Session', undefined, 'w-[8%] truncate')}
            {headerCell('Sessions Left', undefined, 'w-[6%] truncate')}
            {headerCell('NPS', 'averageNPS', 'w-[7%] truncate')}
            {headerCell('Time With Us', 'timeWithUs', 'w-[7%] truncate')}
            {headerCell('Action', undefined, 'w-[7%] truncate')}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {!isLoading ? (
            (churnScores?.users || []).map((row) => (
              <Table.Row key={row._id}>
                <Table.Cell
                  className={mergeClassNames(
                    'w-[12%] truncate cursor-pointer hover:underline',
                    row.isDeactivated && 'text-customRed'
                  )}
                  onClick={() => showMoreHandler(row.userId)}
                >
                  {row.fullName}
                </Table.Cell>
                <Table.Cell className="w-[6%] truncate">{row.graduationYear ?? '-'}</Table.Cell>
                <Table.Cell className="w-[12%] truncate">
                  <ChurnRiskBadge score={row.score} band={row.band} />
                </Table.Cell>
                <Table.Cell
                  className={mergeClassNames(
                    'w-[5%] truncate tabular-nums',
                    typeof row.delta30 === 'number' && row.delta30 > 0 && 'text-customRed',
                    typeof row.delta30 === 'number' && row.delta30 < 0 && 'text-customGreen',
                    (typeof row.delta30 !== 'number' || row.delta30 === 0) && 'text-customGrey'
                  )}
                >
                  {typeof row.delta30 === 'number' && row.delta30 !== 0
                    ? `${row.delta30 > 0 ? '+' : ''}${Math.round(row.delta30)}`
                    : '—'}
                </Table.Cell>
                <Table.Cell className="w-[22%] whitespace-normal text-xs">
                  {renderDrivers(row)}
                </Table.Cell>
                <Table.Cell className="w-[8%] truncate">
                  {relativeDay(row.signals.lastHeldAt)}
                </Table.Cell>
                <Table.Cell className="w-[8%] truncate">
                  {relativeDay(row.signals.nextScheduledAt)}
                </Table.Cell>
                <Table.Cell className="w-[6%] truncate tabular-nums">
                  {row.signals.sessionsRemaining ?? '-'} / {row.signals.sessionsTotal ?? '-'}
                </Table.Cell>
                <Table.Cell className="w-[7%] truncate">
                  <span
                    className={mergeClassNames({
                      'text-customGreen': (row.nps.average ?? 0) > 8,
                      'text-customRed': row.nps.average !== null && row.nps.average <= 8,
                      'text-customGrey': row.nps.average === null,
                    })}
                  >
                    {row.nps.average?.toFixed(1) ?? 'n/a'}
                  </span>
                  {row.nps.last !== null ? (
                    <span className="text-customGrey"> ({row.nps.last} last)</span>
                  ) : null}
                </Table.Cell>
                <Table.Cell className="w-[7%] truncate">
                  {formatTimeWithUs(Number(row.timeWithUs))}
                </Table.Cell>
                <Table.Cell className="w-[7%] truncate">
                  {!row.resolved ? (
                    <button
                      onClick={() => markAsResolved(row._id)}
                      className="text-white bg-green-600 hover:bg-green-700 px-3 py-1 rounded flex items-center justify-center"
                      disabled={resolvingIds.includes(row._id)}
                    >
                      {resolvingIds.includes(row._id) ? (
                        <LoadingSpinner className="w-4 h-4 text-white" />
                      ) : (
                        'Handled'
                      )}
                    </button>
                  ) : (
                    <span className="text-xs text-customGrey">
                      Handled {row.resolvedAt ? moment(row.resolvedAt).fromNow() : ''}
                    </span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))
          ) : (
            <tr>
              <td colSpan={11} className="h-64">
                <div className="w-full h-full flex justify-center items-center">
                  <LoadingSpinner className="w-5 h-5" />
                </div>
              </td>
            </tr>
          )}

          {!isLoading && !churnScores?.users?.length && (
            <tr>
              <td colSpan={11} className="h-64">
                <EmptyStateCard
                  icon={<BlockIcon className="text-customGrey" />}
                  text={'No results.'}
                />
              </td>
            </tr>
          )}
        </Table.Body>
      </Table>
      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          studentId={detailsModalStudentId}
          mentorId={detailsModalMentorId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
